import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiDownload, FiEdit3, FiRefreshCw, FiSave, FiSearch, FiX } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { useAuth } from 'context/AuthProvider';
import { getPortalScope, PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import { showToast } from 'utils/helpers';
import {
  getEmployeeMonthlyIncomeReport,
  updateEmployeeMonthlyIncome,
} from 'services/retail-report.service';
import './employeeMonthlyIncome.scss';

const MONEY_FIELDS = [
  {
    key: 'setup_income',
    apiKey: 'setupincome',
    formApiKey: 'manualsetupincome',
    label: 'Dịch vụ setup / loa RCF / phòng hát / GD VIP',
    formLabel: 'Điều chỉnh thêm cho dịch vụ setup',
    short: 'Dịch vụ setup',
  },
  { key: 'project_income', apiKey: 'projectincome', label: 'Tip ITC / công trình / ánh sáng', short: 'ITC / công trình' },
  { key: 'promoted_product_income', apiKey: 'promotedproductincome', label: 'Tive sản phẩm đẩy quảng cáo', short: 'SP đẩy quảng cáo' },
  { key: 'warranty_income', apiKey: 'warrantyincome', label: 'Tip bảo hành', short: 'Bảo hành' },
  { key: 'dinner_allowance', apiKey: 'dinnerallowance', label: 'Tiền ăn tối', short: 'Ăn tối' },
  { key: 'long_distance_allowance', apiKey: 'longdistanceallowance', label: 'Kỹ thuật giao hàng xe máy trên 30 km', short: 'Đi xa > 30 km' },
  { key: 'setup_return_income', apiKey: 'setupreturnincome', label: 'Tive setup lại', short: 'Setup lại' },
  { key: 'return_goods_income', apiKey: 'returngoodsincome', label: 'Tive trả hàng', short: 'Trả hàng' },
  { key: 'priority_product_income', apiKey: 'priorityproductincome', label: 'Tip sản phẩm ưu tiên', short: 'SP ưu tiên' },
];

const formatNumber = (value) => new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(Number(value) || 0);
const formatMoney = (value, emptyZero = false) => {
  const amount = Number(value) || 0;
  if (emptyZero && amount === 0) return '—';
  return `${formatNumber(amount)} đ`;
};
const parseMoney = (value) => Number(`${value || ''}`.replace(/[^0-9]/g, '')) || 0;
const currentMonth = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const buildFormState = (row) => {
  const source = row || {};
  return MONEY_FIELDS.reduce(
    (result, field) => ({ ...result, [field.key]: formatNumber(source[field.formApiKey || field.apiKey]) }),
    { note: source.note || '' },
  );
};

const EmployeeIncomeModal = ({ row, open, saving, onClose, onSave }) => {
  const [formData, setFormData] = useState(() => buildFormState(row));

  useEffect(() => setFormData(buildFormState(row)), [row]);
  if (!open || !row) return null;

  return (
    <div className='bc_emi_modal_backdrop' role='presentation' onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className='bc_emi_modal' role='dialog' aria-modal='true' aria-labelledby='bc-emi-modal-title'>
        <header className='bc_emi_modal_header'>
          <div>
            <span>Điều chỉnh thu nhập tháng</span>
            <h3 id='bc-emi-modal-title'>{row.username} - {row.fullname}</h3>
          </div>
          <button type='button' className='bc_emi_icon_button' title='Đóng' aria-label='Đóng' onClick={onClose}>
            <FiX />
          </button>
        </header>

        <div className='bc_emi_auto_values'>
          <div><span>Doanh số tự động</span><strong>{formatMoney(row.salesrevenue)}</strong></div>
          <div><span>Hoa hồng bán hàng</span><strong>{formatMoney(row.salescommission)}</strong></div>
          <div><span>Hoa hồng giao hàng</span><strong>{formatMoney(row.deliverycommission)}</strong></div>
          <div><span>Gói dịch vụ đã bán</span><strong>{formatMoney(row.setupserviceincome)}</strong></div>
        </div>

        <div className='bc_emi_modal_body'>
          <p className='bc_emi_form_note'>Chỉ nhập phần điều chỉnh ngoài đơn hàng. Doanh số, hoa hồng, giao hàng và gói dịch vụ đã bán được hệ thống tự tổng hợp.</p>
          <div className='bc_emi_form_grid'>
            {MONEY_FIELDS.map((field) => (
              <label key={field.key} className='bc_emi_field'>
                <span>{field.formLabel || field.label}</span>
                <div className='bc_emi_money_input'>
                  <input
                    inputMode='numeric'
                    value={formData[field.key]}
                    onChange={(event) => setFormData((current) => ({
                      ...current,
                      [field.key]: formatNumber(parseMoney(event.target.value)),
                    }))}
                  />
                  <em>đ</em>
                </div>
              </label>
            ))}
          </div>
          <label className='bc_emi_field bc_emi_note_field'>
            <span>Ghi chú</span>
            <textarea
              rows='3'
              maxLength='1000'
              placeholder='Nội dung đối soát hoặc giải trình khoản phát sinh'
              value={formData.note}
              onChange={(event) => setFormData((current) => ({ ...current, note: event.target.value }))}
            />
          </label>
        </div>

        <footer className='bc_emi_modal_footer'>
          <button type='button' className='bc_emi_button is-secondary' onClick={onClose}><FiX /> Đóng</button>
          <button type='button' className='bc_emi_button is-primary' disabled={saving} onClick={() => onSave(formData)}>
            {saving ? <FiRefreshCw className='is-spinning' /> : <FiSave />} Lưu điều chỉnh
          </button>
        </footer>
      </section>
    </div>
  );
};

const EmployeeMonthlyIncomePage = () => {
  const { user } = useAuth();
  const [portalScope, setPortalScope] = useState(() => getPortalScope());
  const [reportMonth, setReportMonth] = useState(currentMonth);
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [data, setData] = useState({ summary: {}, staff: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);

  const functionSet = useMemo(
    () => new Set((user?.functions || []).map((item) => `${item || ''}`.trim().toUpperCase())),
    [user?.functions],
  );
  const canEdit = Number(user?.isAdministrator) === 1
    || `${user?.user_name || ''}`.toLowerCase() === 'administrator'
    || functionSet.has('SL_RETAIL_EMPLOYEE_MONTHLY_INCOME_EDIT');

  useEffect(() => {
    const handleScopeChange = (event) => setPortalScope(event?.detail || getPortalScope());
    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, handleScopeChange);
    return () => window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, handleScopeChange);
  }, []);

  const loadReport = useCallback(async () => {
    if (!portalScope.store_id) return;
    setLoading(true);
    setError('');
    try {
      const result = await getEmployeeMonthlyIncomeReport({ report_month: reportMonth, keyword });
      setData({ summary: result?.summary || {}, staff: result?.staff || [] });
    } catch (requestError) {
      setError(requestError?.message || 'Không thể tải báo cáo thu nhập nhân viên.');
    } finally {
      setLoading(false);
    }
  }, [keyword, portalScope.store_id, reportMonth]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const totals = useMemo(() => data.staff.reduce((result, row) => {
    MONEY_FIELDS.forEach((field) => { result[field.apiKey] += Number(row[field.apiKey]) || 0; });
    return result;
  }, MONEY_FIELDS.reduce((result, field) => ({ ...result, [field.apiKey]: 0 }), {})), [data.staff]);

  const handleSave = async (formData) => {
    if (!editingRow) return;
    setSaving(true);
    try {
      const payload = MONEY_FIELDS.reduce((result, field) => ({
        ...result,
        [field.key]: parseMoney(formData[field.key]),
      }), {
        report_month: reportMonth,
        note: formData.note?.trim() || null,
      });
      await updateEmployeeMonthlyIncome(editingRow.username, payload);
      showToast.success('Đã cập nhật thu nhập nhân viên.');
      setEditingRow(null);
      await loadReport();
    } catch (requestError) {
      showToast.error(requestError?.message || 'Không thể cập nhật thu nhập nhân viên.');
    } finally {
      setSaving(false);
    }
  };

  const exportExcel = () => {
    const headers = [
      'STT', 'Mã NV', 'Tên nhân viên', 'Chức danh', 'Phòng ban', 'Doanh số bán hàng', 'Số đơn',
      'Hoa hồng bán hàng', 'Tive giao hàng', ...MONEY_FIELDS.map((field) => field.label), 'Tổng thu nhập', 'Ghi chú',
    ];
    const rows = data.staff.map((row, index) => [
      index + 1, row.username, row.fullname, row.positionname, row.departmentname,
      Number(row.salesrevenue) || 0, Number(row.ordercount) || 0, Number(row.salescommission) || 0,
      Number(row.deliverycommission) || 0, ...MONEY_FIELDS.map((field) => Number(row[field.apiKey]) || 0),
      Number(row.totalincome) || 0, row.note || '',
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    worksheet['!cols'] = headers.map((header, index) => ({ wch: index >= 5 && index < headers.length - 1 ? 18 : Math.max(12, Math.min(header.length + 4, 28)) }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Thu nhập nhân viên');
    XLSX.writeFile(workbook, `thu-nhap-nhan-vien-${reportMonth}-${portalScope.store_id}.xlsx`);
  };

  const summary = data.summary || {};
  const hasStore = Boolean(portalScope.store_id);

  return (
    <div className='bw_main_wrapp bc_emi_page'>
      <header className='bc_emi_page_header'>
        <div>
          <span className='bc_emi_eyebrow'>Báo cáo nhân sự theo tháng</span>
          <h2>Thu nhập và doanh số nhân viên</h2>
          <p>Doanh số chia đều theo nhân viên bán hàng trên đơn. Hoa hồng và các khoản thu nhập lấy theo nghiệp vụ đã ghi nhận.</p>
        </div>
        <div className='bc_emi_header_actions'>
          <button type='button' className='bc_emi_button is-secondary' title='Làm mới dữ liệu' onClick={loadReport} disabled={!hasStore || loading}>
            <FiRefreshCw className={loading ? 'is-spinning' : ''} /> Làm mới
          </button>
          <button type='button' className='bc_emi_button is-primary' title='Xuất báo cáo Excel' onClick={exportExcel} disabled={!data.staff.length}>
            <FiDownload /> Xuất Excel
          </button>
        </div>
      </header>

      <section className='bc_emi_filter_bar'>
        <label><span>Tháng báo cáo</span><input type='month' value={reportMonth} onChange={(event) => setReportMonth(event.target.value)} /></label>
        <label className='bc_emi_store_filter'><span>Cửa hàng</span><input value={portalScope.store_name || 'Chưa chọn cửa hàng ở thanh điều hướng'} readOnly /></label>
        <label className='bc_emi_keyword_filter'>
          <span>Tìm nhân viên</span>
          <div><FiSearch /><input value={keywordInput} placeholder='Mã, tên, chức danh hoặc phòng ban' onChange={(event) => setKeywordInput(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && setKeyword(keywordInput.trim())} /></div>
        </label>
        <button type='button' className='bc_emi_icon_button is-filter' title='Lọc báo cáo' aria-label='Lọc báo cáo' onClick={() => setKeyword(keywordInput.trim())}><FiSearch /></button>
      </section>

      {!hasStore ? <section className='bc_emi_state'>Vui lòng chọn cửa hàng trên thanh điều hướng để xem báo cáo.</section> : null}
      {error ? <section className='bc_emi_state is-error'>{error}</section> : null}

      {hasStore && !error ? <>
        <section className='bc_emi_summary' aria-label='Tổng quan báo cáo'>
          <div><span>Nhân viên</span><strong>{formatNumber(summary.staffcount)}</strong><small>trong phạm vi cửa hàng</small></div>
          <div><span>Doanh số</span><strong>{formatMoney(summary.salesrevenue)}</strong><small>{formatNumber(summary.ordercount)} đơn hoàn thành</small></div>
          <div><span>Thu nhập tự động</span><strong>{formatMoney((Number(summary.salescommission) || 0) + (Number(summary.deliverycommission) || 0) + (Number(summary.setupserviceincome) || 0))}</strong><small>hoa hồng + giao hàng + dịch vụ</small></div>
          <div><span>Khoản điều chỉnh</span><strong>{formatMoney(summary.manualincome)}</strong><small>kỹ thuật, phụ cấp, phát sinh</small></div>
          <div className='is-total'><span>Tổng thu nhập</span><strong>{formatMoney(summary.totalincome)}</strong><small>không bao gồm doanh số</small></div>
        </section>

        <section className='bc_emi_table_section'>
          <div className='bc_emi_table_heading'>
            <div><h3>Bảng tổng hợp tháng {reportMonth.split('-').reverse().join('/')}</h3><p>Cuộn ngang để xem đầy đủ các nhóm thu nhập. Số 0 được hiển thị bằng dấu gạch.</p></div>
            <span>{loading ? 'Đang tổng hợp...' : `${formatNumber(data.staff.length)} nhân viên`}</span>
          </div>
          <div className='bc_emi_table_wrap'>
            <table className='bc_emi_table'>
              <thead><tr>
                <th className='is-sticky is-index'>STT</th>
                <th className='is-sticky is-code'>Mã NV</th>
                <th className='is-sticky is-name'>Tên nhân viên</th>
                <th>Chức danh</th><th>Phòng ban</th>
                <th className='is-sales-group'>Doanh số</th><th className='is-sales-group'>Số đơn</th>
                <th className='is-sales-group'>HH bán hàng</th><th className='is-auto-group'>Giao hàng</th>
                {MONEY_FIELDS.map((field) => <th key={field.key} className={field.key === 'setup_income' ? 'is-auto-group' : ''} title={field.label}>{field.short}</th>)}
                <th>Tổng thu nhập</th><th>Ghi chú</th><th>Trạng thái</th><th>Thao tác</th>
              </tr></thead>
              <tbody>
                {!loading && !data.staff.length ? <tr><td colSpan={22} className='bc_emi_empty'>Không có nhân viên phù hợp bộ lọc.</td></tr> : null}
                {data.staff.map((row, index) => (
                  <tr key={row.username}>
                    <td className='is-sticky is-index'>{index + 1}</td>
                    <td className='is-sticky is-code'><strong>{row.username}</strong></td>
                    <td className='is-sticky is-name'><strong>{row.fullname}</strong></td>
                    <td>{row.positionname}</td><td>{row.departmentname}</td>
                    <td className='is-money is-revenue'>{formatMoney(row.salesrevenue, true)}</td>
                    <td className='is-number'>{formatNumber(row.ordercount)}</td>
                    <td className='is-money'>{formatMoney(row.salescommission, true)}</td>
                    <td className='is-money'>{formatMoney(row.deliverycommission, true)}</td>
                    {MONEY_FIELDS.map((field) => <td key={field.key} className='is-money'>{formatMoney(row[field.apiKey], true)}</td>)}
                    <td className='is-money is-total'>{formatMoney(row.totalincome, true)}</td>
                    <td className='is-note' title={row.note || ''}>{row.note || '—'}</td>
                    <td><span className={`bc_emi_status ${row.hasmanualadjustment ? 'is-adjusted' : ''}`}>{row.hasmanualadjustment ? 'Đã bổ sung' : 'Tự động'}</span></td>
                    <td>
                      {canEdit ? <button type='button' className='bc_emi_icon_button is-edit' title={`Điều chỉnh ${row.fullname}`} aria-label={`Điều chỉnh ${row.fullname}`} onClick={() => setEditingRow(row)}><FiEdit3 /></button> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              {data.staff.length ? <tfoot><tr>
                <td colSpan='5'>Tổng cộng</td>
                <td className='is-money'>{formatMoney(summary.salesrevenue)}</td><td className='is-number'>{formatNumber(summary.ordercount)}</td>
                <td className='is-money'>{formatMoney(summary.salescommission)}</td><td className='is-money'>{formatMoney(summary.deliverycommission)}</td>
                {MONEY_FIELDS.map((field) => <td key={field.key} className='is-money'>{formatMoney(totals[field.apiKey])}</td>)}
                <td className='is-money is-total'>{formatMoney(summary.totalincome)}</td><td colSpan='3' />
              </tr></tfoot> : null}
            </table>
          </div>
        </section>
      </> : null}

      <EmployeeIncomeModal row={editingRow} open={Boolean(editingRow)} saving={saving} onClose={() => !saving && setEditingRow(null)} onSave={handleSave} />
    </div>
  );
};

export default EmployeeMonthlyIncomePage;
